// src/encounter/lib/chart-population.ts
//
// PROD-3040 · L1 "Never ask what the chart already knows" (Samer, 6 Oct 2026) · frame v2 only.
// Template items whose answer the record already holds are FILLED from the record, read-only, with the source
// shown ("From the chart"); they are never asked. When the record holds nothing the item stays askable: absence
// in the record is not a "No".
//
// THE MECHANISM (SDC expression-based population, FormBox's model — vendor capability matrix, capability 11):
// the TEMPLATE declares population on the item; this file evaluates it against the visit's launch context. No
// linkId is named anywhere in product code. Two carriers declare it:
//   1. the SDC `sdc-questionnaire-initialExpression` item extension, a FHIRPath over the launch context:
//        %patient.birthDate   → the age on the visit date, matched to the item's age bands
//        %patient.gender      → the registered sex, matched to the item's options
//        %vitals.bmi          → the latest BMI recorded in THIS encounter, matched to the item's bands
//        %problems.hypertension → true when hypertension (ICD-10 I10–I16) is on the ACTIVE problem list;
//                                 never false — an empty list is not "No"
//   2. a terminology binding on the item (`item.code`), for templates whose store keeps codes but not the
//      extension: LOINC 30525-0 Age, 46098-0 Sex, 39156-5 BMI; SNOMED CT
//      38341003 Hypertensive disorder. Each maps to the SAME expression above, so there is one evaluator.
//      A banded scoring item (an age band, a BMI category) carries the Scrips-local binding `cps-chart-fact|age` /
//      `|bmi` in place of the LOINC code (PROD-3118: its band answers have no standard codes).
//
//   3. a `calculatedExpression` over the launch context, with `readOnly` — the carrier QuestionBank STORES today (it
//      drops initialExpression on write; QuestionService keeps calculatedExpression and readOnly). Such an item is a
//      population item, not a computed score: `normalizePopulationItems` (frame v2) turns it into an ordinary,
//      askable item that carries the expression, so it is filled when the record holds the value and asked when not.
//      Bare names are accepted too: %age (years on the visit date), %gender, %bmi, %hypertension.
//
// THE EVALUATOR supports exactly the launch-context paths above (`%name` / `%name.member`). Anything else evaluates to
// nothing and the item stays askable — a template expression this build cannot read never invents an answer.
// When lane C's FHIRPath engine (enableWhen / answerOptionsToggleExpression) lands on main, `evaluate` is the one
// seam to point at it.
//
// THE ANSWER is what the item's own control would write: for a choice, the option's key (`qrOptionValue`); for a
// boolean, `true`; for a number, the number. A value that matches no option, or more than one, fills nothing.
import { qrOptionValue, type QAnswerOption, type QAnswerValue, type QItem } from '@scripsteam/scrips-signal-ds';

export const SDC_INITIAL_EXPRESSION_URL = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-initialExpression';

/** The source label drawn beside a filled item (L1, ENC-L1). */
export const CHART_SOURCE_LABEL = 'From the chart';

/** A template item as the PA carries it: the DS item plus its SDC population expression, when authored. */
export type PopulatedItem = QItem & { initialExpression?: string };

/** What the visit's record holds, for population. Every field optional: absent = the record does not say. */
export interface ChartLaunchContext {
  patient: {
    /** ISO birth date (`YYYY-MM-DD…`), as the record carries it. */
    birthDate?: string;
    /** FHIR AdministrativeGender. */
    gender?: 'male' | 'female' | 'other' | 'unknown';
  };
  /** The visit's date (ISO); the age is taken on this day. Absent → today. */
  visitDate?: string;
  /** The latest readings recorded in this encounter. */
  vitals: { bmi?: number };
  /** The ACTIVE problem list, as ICD-10 codes. */
  problemCodes: readonly string[];
}

/** One filled item: the answer the item's control would write, and where it came from. */
export interface ChartFill {
  value: QAnswerValue;
  source: string;
}

// Keyed by SYSTEM and code (integration, Codex P1): a FHIR code is unique only within its system, so a local code
// "39156-5" on an unrelated question must never be filled with the BMI. A code with no system binds nothing.
const LOINC = 'http://loinc.org';
const SNOMED = 'http://snomed.info/sct';
/** The Scrips-local chart-fact binding system (template-conditions.mjs CHART_FACT_BINDING_SYSTEM, pinned equal by test). */
export const CHART_FACT_BINDING_SYSTEM = 'https://fhir.scrips.com/CodeSystem/cps-chart-fact';
const CODE_BINDINGS: Record<string, string> = {
  [`${LOINC}|30525-0`]: '%patient.birthDate', // LOINC Age
  [`${LOINC}|46098-0`]: '%patient.gender', // LOINC Sex
  // NOT 76689-9 "Sex assigned at birth": a different clinical fact from the registered (administrative) sex, and
  // the record has no birth-sex source — so such an item is asked (Codex P1, 6 Oct).
  [`${LOINC}|39156-5`]: '%vitals.bmi', // LOINC Body mass index
  [`${SNOMED}|38341003`]: '%problems.hypertension', // SNOMED CT Hypertensive disorder
  // PROD-3118 · a BANDED scoring question (the CDC "Age band", "BMI category") is not the standard concept and has no
  // standard answer list, so Template Studio will not publish it under the LOINC code with placeholder answers. Such an
  // item carries a Scrips-local binding instead (scripts/lib/template-conditions.mjs C1b), read exactly like the code.
  [`${CHART_FACT_BINDING_SYSTEM}|age`]: '%patient.birthDate',
  [`${CHART_FACT_BINDING_SYSTEM}|bmi`]: '%vitals.bmi',
};

/** The population expression an item declares: its SDC initialExpression, a launch-context calculatedExpression,
 *  else its terminology binding. */
export function populationExpressionOf(item: PopulatedItem): string | undefined {
  const expr = item.initialExpression?.trim();
  if (expr) return expr;
  if (isLaunchContextExpression(item.calculatedExpression)) return (item.calculatedExpression as string).trim();
  for (const c of item.code ?? []) {
    const bound = CODE_BINDINGS[`${String(c.system ?? '').trim()}|${String(c.code ?? '').trim()}`];
    if (bound) return bound;
  }
  return undefined;
}

/** ICD-10 I10–I16 (essential, hypertensive heart / kidney / heart-and-kidney disease, secondary, crisis). */
export function hypertensionOnList(codes: readonly string[]): boolean {
  return codes.some((c) => /^I1[0-6](?:\.|$|[0-9A-Z])/i.test(c.trim()));
}

/** Whole years between `birthDate` and `onDate` (both ISO); undefined when either is unreadable. */
export function ageOnDate(birthDate: string, onDate?: string): number | undefined {
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (!b) return undefined;
  const on = onDate ? /^(\d{4})-(\d{2})-(\d{2})/.exec(onDate) : undefined;
  const now = new Date();
  const [y, m, d] = on ? [Number(on[1]), Number(on[2]), Number(on[3])] : [now.getFullYear(), now.getMonth() + 1, now.getDate()];
  let age = y - Number(b[1]);
  if (m < Number(b[2]) || (m === Number(b[2]) && d < Number(b[3]))) age -= 1;
  return age >= 0 ? age : undefined;
}

/** The launch-context environment the expressions read. Only facts the record holds are present. */
function environment(ctx: ChartLaunchContext): Record<string, unknown> {
  const hypertension = hypertensionOnList(ctx.problemCodes) ? true : undefined;
  return {
    patient: { birthDate: ctx.patient.birthDate, gender: ctx.patient.gender },
    vitals: { bmi: ctx.vitals.bmi },
    problems: { hypertension },
    // Named variables (bare): the same facts.
    age: ctx.patient.birthDate ? ageOnDate(ctx.patient.birthDate, ctx.visitDate) : undefined,
    gender: ctx.patient.gender,
    bmi: ctx.vitals.bmi,
    hypertension,
  };
}

/** The launch-context roots an expression may read. Anything else is not a population expression. */
const LAUNCH_ROOTS = ['patient', 'vitals', 'problems', 'age', 'gender', 'bmi', 'hypertension'];
const LAUNCH_PATH = /^%([A-Za-z]\w*)((?:\.[A-Za-z]\w*)*)$/;

/** Is `expression` a launch-context path this file evaluates (population), rather than a computed score? */
export function isLaunchContextExpression(expression: string | null | undefined): boolean {
  const m = LAUNCH_PATH.exec(String(expression ?? '').trim());
  return !!m && LAUNCH_ROOTS.includes(m[1]);
}

/** Evaluate a launch-context path (`%name` / `%name.member`). Anything else → undefined (the item stays askable). */
export function evaluate(expression: string, ctx: ChartLaunchContext): unknown {
  const m = LAUNCH_PATH.exec(expression.trim());
  if (!m || !LAUNCH_ROOTS.includes(m[1])) return undefined;
  let focus: unknown = environment(ctx)[m[1]];
  for (const member of m[2] ? m[2].slice(1).split('.') : []) {
    if (focus == null || typeof focus !== 'object') return undefined;
    focus = (focus as Record<string, unknown>)[member];
  }
  return focus === null || focus === '' ? undefined : focus;
}

const optionText = (o: QAnswerOption): string => (o.valueCoding?.display ?? o.valueString ?? o.valueCoding?.code ?? '').trim();

/** An option's numeric band from its display ("Younger than 40", "40 to 49", "60 or older", "Below 25"). */
export function optionBand(o: QAnswerOption): { min: number; max: number } | undefined {
  const t = optionText(o).toLowerCase();
  const num = '(\\d+(?:\\.\\d+)?)';
  let m = new RegExp(`(?:younger than|under|less than|below|<)\\s*${num}`).exec(t);
  if (m) return { min: -Infinity, max: Number(m[1]) - 1e-9 };
  m = new RegExp(`${num}\\s*(?:to|–|-)\\s*${num}`).exec(t);
  if (m) return { min: Number(m[1]), max: Number(m[2]) };
  m = new RegExp(`${num}\\s*(?:or older|or higher|or more|or over|and over|and above|\\+)`).exec(t);
  if (m) return { min: Number(m[1]), max: Infinity };
  m = new RegExp(`(?:at least|>=|≥)\\s*${num}`).exec(t);
  if (m) return { min: Number(m[1]), max: Infinity };
  return undefined;
}

/**
 * The option whose band holds `n`. Bands are read as HALF-OPEN, from each band's lower bound up to the next
 * band's: "25 to 29.9" holds 29.95 and "Below 25" holds 24.96, with no rounding (Codex P2: rounding moved 24.96
 * into "25 to 29.9"). Above the last band's own upper bound, or with two bands starting at the same value, nothing
 * is filled.
 */
function bandOption(options: readonly QAnswerOption[], n: number): QAnswerOption | undefined {
  const bands = options
    .map((o) => ({ o, b: optionBand(o) }))
    .filter((x): x is { o: QAnswerOption; b: { min: number; max: number } } => !!x.b)
    .sort((a, b) => a.b.min - b.b.min);
  if (new Set(bands.map((x) => x.b.min)).size !== bands.length) return undefined;
  // The band table must be whole (integration, Codex P1): with a gap ("Below 25", "30 or higher") or an overlap, a
  // value could be read into the wrong band and filled read-only as chart data. Such a table fills nothing.
  if (!bandsContiguous(bands.map((x) => x.b))) return undefined;
  let hit: (typeof bands)[number] | undefined;
  for (const x of bands) if (n >= x.b.min) hit = x;
  if (!hit) return undefined;
  const isLast = hit === bands[bands.length - 1];
  if (isLast && n > hit.b.max) return undefined;
  return hit.o;
}

/** One step of a band's written precision: "49" → 1, "29.9" → 0.1, a "below N" bound (N − ε) → ε. */
function precisionStep(max: number): number {
  const text = String(max);
  const dot = text.indexOf('.');
  return dot < 0 ? 1 : 10 ** -(text.length - dot - 1);
}

/** Each band starts where the one before it ends: no gap wider than its written precision, and no overlap. */
export function bandsContiguous(bands: readonly { min: number; max: number }[]): boolean {
  for (let i = 1; i < bands.length; i += 1) {
    const prev = bands[i - 1];
    const cur = bands[i];
    if (!Number.isFinite(prev.max)) return false;
    const gap = cur.min - prev.max;
    if (gap < -1e-6 || gap > precisionStep(prev.max) + 1e-6) return false;
  }
  return true;
}

const SEX_WORDS: Record<'male' | 'female', readonly string[]> = { male: ['male', 'man', 'm'], female: ['female', 'woman', 'f'] };
const YES_WORDS = ['yes', 'true', 'y'];

function wordOption(options: readonly QAnswerOption[], words: readonly string[]): QAnswerOption | undefined {
  const hits = options.filter((o) => {
    const keys = [o.valueCoding?.code, o.valueCoding?.display, o.valueString].map((k) => String(k ?? '').trim().toLowerCase());
    return keys.some((k) => words.includes(k));
  });
  return hits.length === 1 ? hits[0] : undefined;
}

/**
 * The answer the record gives `item`, or undefined when the item declares no population, the record holds
 * nothing for it, or the value fits none (or more than one) of its options. Never a "No" from absence.
 */
export function chartFillFor(item: PopulatedItem, ctx: ChartLaunchContext): ChartFill | undefined {
  const expression = populationExpressionOf(item);
  if (!expression) return undefined;
  let value = evaluate(expression, ctx);
  if (value === undefined || value === false) return undefined;
  // A birth date populates as the age on the visit date (the item asks an age, never a date of birth here).
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) && /birthDate$/.test(expression.trim())) {
    if (item.type === 'date' || item.type === 'dateTime') return { value: value.slice(0, 10), source: CHART_SOURCE_LABEL };
    // The age is taken ON the visit date; with no readable visit date nothing is filled (never "today" for an
    // older visit — Codex P2, 6 Oct).
    if (!ctx.visitDate || !/^\d{4}-\d{2}-\d{2}/.test(ctx.visitDate)) return undefined;
    const age = ageOnDate(value, ctx.visitDate);
    if (age === undefined) return undefined;
    value = age;
  }
  const options = item.answerOption ?? [];
  if ((item.type === 'choice' || item.type === 'open-choice') && options.length > 0) {
    let chosen: QAnswerOption | undefined;
    if (typeof value === 'number' && Number.isFinite(value)) chosen = bandOption(options, value);
    else if (value === 'male' || value === 'female') chosen = wordOption(options, SEX_WORDS[value]);
    else if (value === true) chosen = wordOption(options, YES_WORDS);
    if (!chosen) return undefined;
    const key = qrOptionValue(chosen);
    return { value: item.repeats ? [key] : key, source: CHART_SOURCE_LABEL };
  }
  if (item.type === 'boolean') return value === true ? { value: true, source: CHART_SOURCE_LABEL } : undefined;
  if ((item.type === 'integer' || item.type === 'decimal') && typeof value === 'number' && Number.isFinite(value)) {
    return { value: item.type === 'integer' ? Math.trunc(value) : value, source: CHART_SOURCE_LABEL };
  }
  if ((item.type === 'string' || item.type === 'text') && typeof value === 'string') return { value, source: CHART_SOURCE_LABEL };
  return undefined;
}

/** Every filled item under `items`, by linkId. */
export function chartFills(items: readonly QItem[], ctx: ChartLaunchContext): Record<string, ChartFill> {
  const out: Record<string, ChartFill> = {};
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      const fill = chartFillFor(it as PopulatedItem, ctx);
      if (fill) out[it.linkId] = fill;
      if (it.item?.length) walk(it.item);
    }
  };
  walk(items);
  return out;
}

/** The record's sex label ('M' / 'F' / 'Male' / 'Female' …) → FHIR AdministrativeGender, or undefined. */
export function genderOf(sex: string | null | undefined): ChartLaunchContext['patient']['gender'] {
  const s = String(sex ?? '').trim().toLowerCase();
  if (s === 'm' || s === 'male') return 'male';
  if (s === 'f' || s === 'female') return 'female';
  return undefined;
}

/**
 * Frame v2: a template item carried as `calculatedExpression` over the launch context + `readOnly` (the carrier
 * QuestionBank stores) becomes an ordinary item that carries the expression as its `initialExpression`. So the
 * note fills it from the record (read-only, with its source) when the record holds the value, and ASKS it — a
 * normal, saved answer — when the record holds nothing (absence is not "No"). A calculatedExpression over answers
 * (a score) is untouched. The same object comes back when nothing changes.
 */
export function normalizePopulationItems<Q extends { item: QItem[] }>(questionnaire: Q): Q {
  let changed = false;
  const walk = (items: readonly QItem[]): QItem[] =>
    items.map((it) => {
      let next: QItem = it;
      if (isLaunchContextExpression(it.calculatedExpression)) {
        const { calculatedExpression, readOnly: _readOnly, ...rest } = it as PopulatedItem;
        void _readOnly;
        next = { ...rest, initialExpression: (it as PopulatedItem).initialExpression ?? (calculatedExpression as string).trim() } as PopulatedItem;
        changed = true;
      }
      if (it.item?.length) {
        const kids = walk(it.item);
        if (kids.some((k, i) => k !== it.item?.[i])) next = { ...next, item: kids };
      }
      return next;
    });
  const item = walk(questionnaire.item);
  return changed ? { ...questionnaire, item } : questionnaire;
}
